import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../../services/api';
import { CrmButton, CrmModal } from '../ui';
import MediaAssetLibrary, { mediaLabel, mediaUrl } from '../media/MediaAssetLibrary';
import './media-asset-picker.scss';

const normalizeIds = (value, multiple) => {
  if (multiple) return Array.isArray(value) ? value.filter(Boolean).map(String) : [];
  return value ? [String(value)] : [];
};

const PickerModal = ({ open, onClose, title, mediaType, multiple, storefrontId, initialIds, note, onUse }) => {
  const [draftAssets, setDraftAssets] = useState([]);

  useEffect(() => {
    if (open) setDraftAssets([]);
  }, [open, initialIds]);

  const selectedIds = useMemo(() => draftAssets.length ? draftAssets.map((asset) => asset.id) : initialIds, [draftAssets, initialIds]);
  const usable = draftAssets.length > 0 && draftAssets.every((asset) => asset?.url);

  return (
    <CrmModal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={multiple ? 'Select one or more assets from the media library.' : 'Select an asset from the media library.'}
      size="xl"
      className="media-picker-modal"
      backdropClassName="media-picker-backdrop"
      footer={(
        <div className="media-picker-modal__footer-content">
          <span className="media-picker-modal__note">{note || (multiple ? 'Select the assets you want to use.' : 'Select an asset, then confirm your choice.')}</span>
          <CrmButton variant="primary" disabled={!usable} onClick={() => onUse(draftAssets)}>
            {multiple ? `Use selected${draftAssets.length ? ` (${draftAssets.length})` : ''}` : 'Use this'}
          </CrmButton>
        </div>
      )}
    >
      <MediaAssetLibrary
        mediaType={mediaType}
        multiple={multiple}
        storefrontId={storefrontId}
        initialIds={initialIds}
        selectedIds={selectedIds}
        onSelectionChange={setDraftAssets}
        showSelectionAction={false}
      />
    </CrmModal>
  );
};

export const MediaAssetInput = forwardRef(function MediaAssetInput({ mediaType = 'IMAGE', multiple = false, onSelect, storefrontId, value, helperText }, ref) {
  const [open, setOpen] = useState(false);
  const initialIds = useMemo(() => normalizeIds(value, multiple), [value, multiple]);
  useImperativeHandle(ref, () => ({ click: () => setOpen(true) }), []);

  return (
    <PickerModal
      open={open}
      onClose={() => setOpen(false)}
      title={`Choose ${mediaType === 'VIDEO' ? 'video' : 'media'}`}
      mediaType={mediaType}
      multiple={multiple}
      storefrontId={storefrontId}
      initialIds={initialIds}
      note={helperText}
      onUse={(assets) => {
        onSelect?.(multiple ? assets : assets[0]);
        setOpen(false);
      }}
    />
  );
});

export default function MediaAssetPicker({
  label = 'Media',
  mediaType = 'IMAGE',
  value = '',
  currentUrl = '',
  onChange,
  helperText,
  multiple = false,
  storefrontId,
  showPreview = true,
  showClear = true,
}) {
  const [open, setOpen] = useState(false);
  const [resolvedAssets, setResolvedAssets] = useState([]);
  const initialIds = useMemo(() => normalizeIds(value, multiple), [value, multiple]);

  useEffect(() => {
    let active = true;
    const ids = normalizeIds(value, multiple);

    if (!ids.length) {
      setResolvedAssets(currentUrl ? [{ id: value || 'current', url: currentUrl }] : []);
      return () => { active = false; };
    }

    if (!multiple && currentUrl) {
      setResolvedAssets([{ id: value, url: currentUrl }]);
      return () => { active = false; };
    }

    Promise.all(ids.map(async (id) => {
      try {
        const { data } = await api.get(`/media-assets/${id}`);
        return data;
      } catch {
        return null;
      }
    })).then((assets) => {
      if (active) setResolvedAssets(assets.filter(Boolean));
    });

    return () => { active = false; };
  }, [value, currentUrl, multiple]);

  const clear = () => {
    setResolvedAssets([]);
    onChange?.(multiple ? [] : '', multiple ? [] : null);
  };

  return (
    <div className="media-picker-field settings-form-grid__wide">
      {label ? <div className="media-picker-field__label">{label}</div> : null}

      {showPreview && resolvedAssets.length ? (
        <div className={`media-picker-field__previews ${multiple ? 'is-multiple' : ''}`}>
          {resolvedAssets.map((asset) => (
            <div key={asset.id || asset.url} className="media-picker-field__preview">
              {mediaType === 'VIDEO' ? (
                <video src={mediaUrl(asset.url)} controls preload="metadata" />
              ) : (
                <img src={mediaUrl(asset.url)} alt={asset.alt_text || mediaLabel(asset) || 'Selected media'} />
              )}
              <span title={mediaLabel(asset)}>{mediaLabel(asset)}</span>
            </div>
          ))}
        </div>
      ) : showPreview ? (
        <div className="media-picker-field__empty">
          <ImageOutlinedIcon fontSize="small" />
          <span>No {mediaType === 'VIDEO' ? 'video' : 'image'} selected</span>
        </div>
      ) : null}

      <div className="media-picker-field__actions">
        <CrmButton variant="secondary" onClick={() => setOpen(true)}>
          Choose {mediaType === 'VIDEO' ? 'video' : 'image'}{multiple ? 's' : ''}
        </CrmButton>
        {showClear && (initialIds.length || currentUrl) ? (
          <CrmButton variant="tertiary" startIcon={<DeleteOutlineIcon fontSize="small" />} onClick={clear}>Clear</CrmButton>
        ) : null}
      </div>

      {helperText ? <div className="media-picker-field__help">{helperText}</div> : null}

      <PickerModal
        open={open}
        onClose={() => setOpen(false)}
        title={label || `Choose ${mediaType === 'VIDEO' ? 'video' : 'media'}`}
        mediaType={mediaType}
        multiple={multiple}
        storefrontId={storefrontId}
        initialIds={initialIds}
        note={helperText}
        onUse={(assets) => {
          const nextValue = multiple ? assets.map((asset) => asset.id) : assets[0]?.id || '';
          const nextAsset = multiple ? assets : assets[0] || null;
          setResolvedAssets(assets);
          onChange?.(nextValue, nextAsset);
          setOpen(false);
        }}
      />
    </div>
  );
}
